import React, { useContext, useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Divider,
  Stack,
  TextField,
  CircularProgress
} from '@mui/material'
import { AuthContext } from 'contexts/AuthContext'
import NameFields from 'components/NameFields'
import EditPasswordForm from 'pages/MyAccount/EditPasswordForm'
import { formatPhoneNumber, validatePhoneNumber } from 'utils/formatUtils'
import { LoadingContext } from 'contexts/LoadingContext'
import { useTranslation } from 'react-i18next'

const AccountForm: React.FC<{ onCancel: () => void }> = ({ onCancel }) => {
  const { t } = useTranslation()
  const { updateAccountInfo, accountInfo } = useContext(AuthContext)
  const { showLoading, stopLoading } = useContext(LoadingContext)

  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    companyName: '',
    salesRep: ''
  })

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    companyName: ''
  })

  const [isEditingName, setIsEditingName] = useState(false)
  const [isEditingPhoneName, setIsEditingPhoneName] = useState(false)
  const [isEditingCompanyName, setIsEditingCompanyName] = useState(false)
  const [isEditingPassword, setIsEditingPassword] = useState(false)

  useEffect(() => {
    if (accountInfo) {
      setFormValues({
        firstName: accountInfo.firstName,
        lastName: accountInfo.lastName,
        email: accountInfo.email,
        phoneNumber: formatPhoneNumber(accountInfo.phoneNumber),
        companyName: accountInfo.companyName,
        salesRep: accountInfo.salesRep
      })
    }
  }, [accountInfo])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    const newValue = name === 'phoneNumber' ? formatPhoneNumber(value) : value
    setFormValues(prev => ({ ...prev, [name]: newValue }))
  }

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    let error = ''
    if (name === 'phoneNumber') {
      error = validatePhoneNumber(value)
        ? ''
        : 'Phone Number must be in the format 123-456-7890.'
    } else if (name === 'companyName') {
      error = value.trim() ? '' : 'Company Name is required.'
    } else {
      error = value.trim() ? '' : `${name} is required.`
    }
    setErrors(prev => ({ ...prev, [name]: error }))
  }

  const handleNameSave = async () => {
    const newErrors = {
      firstName: formValues.firstName.trim() ? '' : 'First name is required.',
      lastName: formValues.lastName.trim() ? '' : 'Last name is required.'
    }
    setErrors(prev => ({ ...prev, ...newErrors }))
    if (!newErrors.firstName && !newErrors.lastName) {
      showLoading()
      await updateAccountInfo({
        firstName: formValues.firstName,
        lastName: formValues.lastName
      })
      setIsEditingName(false)
      stopLoading()
    }
  }

  const handlePhoneSave = async () => {
    const error = validatePhoneNumber(formValues.phoneNumber)
      ? ''
      : 'Phone Number must be in the format 123-456-7890.'
    setErrors(prev => ({ ...prev, phoneNumber: error }))
    if (!error) {
      showLoading()
      await updateAccountInfo({ phoneNumber: formValues.phoneNumber })
      setIsEditingPhoneName(false)
      stopLoading()
    }
  }

  const handleCompanySave = async () => {
    const error = formValues.companyName.trim()
      ? ''
      : 'Company Name is required.'
    setErrors(prev => ({ ...prev, companyName: error }))
    if (!error) {
      showLoading()
      await updateAccountInfo({ companyName: formValues.companyName })
      setIsEditingCompanyName(false)
      stopLoading()
    }
  }

  if (!accountInfo) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          padding: '20px',
          maxWidth: '700px',
          margin: '0 auto'
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ padding: '20px', maxWidth: '700px', margin: '0 auto' }}>
      {isEditingPassword ? (
        <EditPasswordForm onCancel={() => setIsEditingPassword(false)} />
      ) : (
        <>
          <Box
            sx={{ minHeight: '100px', display: 'flex', alignItems: 'center' }}
          >
            <Stack direction='row' justifyContent='space-between' width='100%'>
              <Stack spacing={1} flex={1}>
                <Typography variant='h3'>
                  {t('myAccount.accountForm.name')}
                </Typography>
                {!isEditingName ? (
                  <Typography>{`${formValues.firstName} ${formValues.lastName}`}</Typography>
                ) : (
                  <Box sx={{ maxWidth: 300 }}>
                    <NameFields
                      formValues={formValues}
                      errors={errors}
                      handleInputChange={handleInputChange}
                      handleBlur={handleBlur}
                    />
                  </Box>
                )}
              </Stack>
              <Box>
                {!isEditingName ? (
                  <Button onClick={() => setIsEditingName(true)}>
                    {t('myAccount.accountForm.edit')}
                  </Button>
                ) : (
                  <Stack direction='row' spacing={1}>
                    <Button
                      variant='outlined'
                      sx={{ width: '70px' }}
                      onClick={() => {
                        setIsEditingName(false)
                        setFormValues(prev => ({
                          ...prev,
                          firstName: accountInfo?.firstName || '',
                          lastName: accountInfo?.lastName || ''
                        }))
                      }}
                    >
                      {t('myAccount.accountForm.cancel')}
                    </Button>
                    <Button
                      variant='contained'
                      sx={{ width: '70px' }}
                      onClick={handleNameSave}
                    >
                      {t('myAccount.accountForm.save')}
                    </Button>
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>
          <Divider />

          <Box
            sx={{ minHeight: '100px', display: 'flex', alignItems: 'center' }}
          >
            <Stack direction='row' justifyContent='space-between' width='100%'>
              <Stack spacing={1} flex={1}>
                <Typography variant='h3'>
                  {t('myAccount.accountForm.phoneNumber')}
                </Typography>
                {!isEditingPhoneName ? (
                  <Typography>{formValues.phoneNumber}</Typography>
                ) : (
                  <TextField
                    name='phoneNumber'
                    value={formValues.phoneNumber}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder='123-456-7890'
                    fullWidth
                    margin='dense'
                    variant='outlined'
                    required
                    error={Boolean(errors.phoneNumber)}
                    helperText={errors.phoneNumber || ''}
                    size='small'
                    sx={{ maxWidth: 140 }}
                  />
                )}
              </Stack>
              <Box>
                {!isEditingPhoneName ? (
                  <Button onClick={() => setIsEditingPhoneName(true)}>
                    {t('myAccount.accountForm.edit')}
                  </Button>
                ) : (
                  <Stack direction='row' spacing={1}>
                    <Button
                      variant='outlined'
                      sx={{ width: '70px' }}
                      onClick={() => {
                        setIsEditingPhoneName(false)
                        setFormValues(prev => ({
                          ...prev,
                          phoneNumber: formatPhoneNumber(
                            accountInfo?.phoneNumber || ''
                          )
                        }))
                      }}
                    >
                      {t('myAccount.accountForm.cancel')}
                    </Button>
                    <Button
                      variant='contained'
                      sx={{ width: '70px' }}
                      onClick={handlePhoneSave}
                    >
                      {t('myAccount.accountForm.save')}
                    </Button>
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>
          <Divider />

          <Box
            sx={{ minHeight: '100px', display: 'flex', alignItems: 'center' }}
          >
            <Stack direction='row' justifyContent='space-between' width='100%'>
              <Stack spacing={1} flex={1}>
                <Typography variant='h3'>
                  {t('myAccount.accountForm.companyName')}
                </Typography>
                {!isEditingCompanyName ? (
                  <Typography>{formValues.companyName}</Typography>
                ) : (
                  <TextField
                    name='companyName'
                    value={formValues.companyName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder='Company Name'
                    fullWidth
                    margin='dense'
                    variant='outlined'
                    required
                    error={Boolean(errors.companyName)}
                    helperText={errors.companyName || ''}
                    size='small'
                    sx={{ maxWidth: 200 }}
                  />
                )}
              </Stack>
              <Box>
                {!isEditingCompanyName ? (
                  <Button onClick={() => setIsEditingCompanyName(true)}>
                    {t('myAccount.accountForm.edit')}
                  </Button>
                ) : (
                  <Stack direction='row' spacing={1}>
                    <Button
                      variant='outlined'
                      sx={{ width: '70px' }}
                      onClick={() => {
                        setIsEditingCompanyName(false)
                        setFormValues(prev => ({
                          ...prev,
                          companyName: accountInfo?.companyName || ''
                        }))
                      }}
                    >
                      {t('myAccount.accountForm.cancel')}
                    </Button>
                    <Button
                      variant='contained'
                      sx={{ width: '70px' }}
                      onClick={handleCompanySave}
                    >
                      {t('myAccount.accountForm.save')}
                    </Button>
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>
          <Divider />

          <Box
            sx={{ minHeight: '100px', display: 'flex', alignItems: 'center' }}
          >
            <Stack spacing={1} flex={1}>
              <Typography variant='h3'>
                {t('myAccount.accountForm.email')}
              </Typography>
              <Typography>{formValues.email}</Typography>
            </Stack>
          </Box>
          <Divider />

          <Box
            sx={{ minHeight: '100px', display: 'flex', alignItems: 'center' }}
          >
            <Stack spacing={1} flex={1}>
              <Typography variant='h3'>
                {t('myAccount.accountForm.salesRep')}
              </Typography>
              <Typography>{formValues.salesRep}</Typography>
            </Stack>
          </Box>
          <Divider />

          <Box
            sx={{ minHeight: '100px', display: 'flex', alignItems: 'center' }}
          >
            <Stack direction='row' justifyContent='space-between' width='100%'>
              <Stack spacing={1} flex={1}>
                <Typography variant='h3'>
                  {t('myAccount.accountForm.password')}
                </Typography>
                <Typography>*********</Typography>
              </Stack>
              <Button onClick={() => setIsEditingPassword(true)}>
                {t('myAccount.accountForm.edit')}
              </Button>
            </Stack>
          </Box>
          <Divider />

          <Box sx={{ mt: 2 }}>
            <Button variant='outlined' onClick={onCancel}>
              {t('myAccount.accountForm.backToAccount')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  )
}

export default AccountForm
